/**
 * Download Card — what "send me that as Excel" leaves in the chat.
 *
 * The agent resolves *which* table was meant and offers it here; the file
 * itself streams from the frame-data endpoint under the analyst's own token
 * when they press a button. Three things follow from that:
 *
 * - Nothing downloads on render. The agent proposes, the analyst disposes.
 * - We may have guessed. When the thread holds other tables, "Not this one?"
 *   opens them inline — a wrong guess costs one click, not another turn.
 * - The offer outlives its data. Frames live in the session's DuckDB, so a
 *   card reopened weeks later gets a 410; that reads as "expired", not as a
 *   broken button.
 *
 * The look is the UI kit's `FileOfferCard`; this is only the downloading.
 */

import React, { useContext, useState } from 'react';
import { FileOfferCard } from '../../../ui-kit';
import { downloadFrame, type ExportFormat } from '../../../utils/frameExport';
import { SnackbarContext } from '../../../ui/SnackbarProvider';
import type { DownloadOffer } from '../../../types/download';

interface Props {
  offer: DownloadOffer;
  threadId: string | null;
}

/** "12,431 rows · 8 columns", skipping either half when we don't know it. */
function describe(rowCount: number, columnCount: number): string {
  const parts: string[] = [];
  if (rowCount > 0) parts.push(`${rowCount.toLocaleString()} row${rowCount === 1 ? '' : 's'}`);
  if (columnCount > 0) parts.push(`${columnCount} column${columnCount === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

const labelFor = (f: ExportFormat) => (f === 'xlsx' ? 'Excel' : 'CSV');

export default function DownloadCard({ offer, threadId }: Props) {
  const { notify } = useContext(SnackbarContext);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  const run = async (frameId: string, title: string, format: ExportFormat) => {
    if (!threadId || busyKey) return;
    const key = `${frameId}:${format}`;
    setBusyKey(key);
    try {
      const { rowCount } = await downloadFrame(frameId, threadId, format, title);
      notify(
        rowCount != null
          ? `Downloaded ${rowCount.toLocaleString()} rows`
          : 'Download ready',
        'success',
      );
    } catch (e: any) {
      if (e?.status === 410) {
        // The session's frame is gone. Retrying cannot help, so stop
        // offering the button and say why.
        setExpired(true);
      } else {
        notify(e?.message || 'Could not prepare the download', 'error');
      }
    } finally {
      setBusyKey(null);
    }
  };

  const meta = describe(offer.rowCount, offer.columnCount);
  const capped = !!offer.maxRows && offer.rowCount > offer.maxRows;
  // Whichever format they asked for leads; the other stays one click away,
  // because "as Excel" and "as CSV" are the same request with different tools.
  const primary: ExportFormat = offer.format === 'csv' ? 'csv' : 'xlsx';
  const secondary: ExportFormat = primary === 'xlsx' ? 'csv' : 'xlsx';

  const action = (format: ExportFormat) => ({
    key: format,
    label: labelFor(format),
    onDownload: () => void run(offer.frameId, offer.title, format),
    loading: busyKey === `${offer.frameId}:${format}`,
  });

  return (
    <FileOfferCard
      title={offer.title}
      meta={capped ? `${meta} · first ${offer.maxRows!.toLocaleString()} rows` : meta || 'Ready to download'}
      unavailable={expired ? 'No longer available — ask me to run it again' : undefined}
      primary={action(primary)}
      others={[action(secondary)]}
      disabled={!threadId || !!busyKey}
      alternatives={offer.alternatives.map((alt) => ({
        key: alt.frameId,
        title: alt.title,
        meta: describe(alt.rowCount, alt.columnCount) || undefined,
        formats: (['xlsx', 'csv'] as ExportFormat[]).map((f) => ({
          key: f,
          label: f === 'xlsx' ? 'Excel (.xlsx)' : 'CSV',
          onDownload: () => void run(alt.frameId, alt.title, f),
        })),
      }))}
    />
  );
}
